/**
 * Seeded random number generator for deterministic level generation
 * Uses xorshift algorithm for fast, good-quality pseudo-random numbers
 */
export class SeededRandom {
    private seed: number;

    constructor(seed: number | string) {
        if (typeof seed === 'string') {
            // Convert string to number hash
            let hash = 0;
            for (let i = 0; i < seed.length; i++) {
                const char = seed.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }
            this.seed = hash >>> 0; // Ensure positive
        } else {
            this.seed = seed >>> 0; // Ensure positive
        }
    }

    /**
     * Generate next random number between 0 and 1
     */
    next(): number {
        let x = this.seed;
        x ^= x << 13;
        x ^= x >> 17;
        x ^= x << 5;
        this.seed = x >>> 0; // Ensure positive
        return (x >>> 0) / 0xFFFFFFFF;
    }

    /**
     * Generate random integer between min (inclusive) and max (exclusive)
     */
    nextInt(min: number, max: number): number {
        return Math.floor(this.next() * (max - min)) + min;
    }

    /**
     * Generate random float between min (inclusive) and max (exclusive)
     */
    nextFloat(min: number, max: number): number {
        return this.next() * (max - min) + min;
    }

    /**
     * Choose random element from array
     */
    choice<T>(array: T[]): T {
        return array[this.nextInt(0, array.length)];
    }

    /**
     * Shuffle array deterministically (Fisher-Yates)
     */
    shuffle<T>(array: T[]): T[] {
        const result = [...array];
        for (let i = result.length - 1; i > 0; i--) {
            const j = this.nextInt(0, i + 1);
            [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
    }

    /**
     * Get current seed (useful for debugging)
     */
    getSeed(): number {
        return this.seed;
    }
}

